import React, { useState, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  CircularProgress,
  makeStyles
} from '@material-ui/core';
import { Notification, useNotify } from 'react-admin';
import axios from 'axios';
import { ENDPOINTS } from '../apiConstants';
import { getConfigGet, getConfigPatch } from '../getConfig';

// Unified table component with configurable styling
const UnifiedTable = React.memo<{ 
  data: any[][], 
  title: string, 
  actionButtons?: React.ReactNode,
  variant?: 'default' | 'referral' | 'beforeAfter',
  beforeData?: any[][],
  afterData?: any[][]
}>(({ data, title, actionButtons, variant = 'default', beforeData, afterData }) => {
  const classes = useStyles();
  
  if (!data || data.length === 0) return null;
  
  // Determine styling based on variant
  const getTableStyles = () => {
    switch (variant) {
      case 'referral':
        return {
          tableContainer: classes.referralTableContainer,
          table: classes.referralTable,
          card: classes.rewardsCard,
          showActions: false
        };
      case 'beforeAfter':
        return {
          tableContainer: classes.tableContainer,
          table: classes.compactTable,
          card: classes.rewardsCard,
          showActions: true
        };
      default:
        return {
          tableContainer: classes.tableContainer,
          table: classes.compactTable,
          card: classes.rewardsCard,
          showActions: true
        };
    }
  };
  
  const styles = getTableStyles();
  
  // Render single table
  const renderTable = (tableData: any[][], subtitle?: string) => {
    const tableHeaders = tableData[0];
    const tableRows = tableData.slice(1);
    
    return (
      <Card className={styles.card}>
        <CardContent>
          {subtitle && (
            <Typography className={variant === 'beforeAfter' ? classes.beforeAfterTitle : classes.sectionTitle}>
              {subtitle}
            </Typography>
          )}
          <TableContainer component={Paper} className={styles.tableContainer}>
            <Table size="small" className={styles.table}>
              <TableHead>
                <TableRow>
                  {tableHeaders.map((header: string, index: number) => (
                    <TableCell 
                      key={index} 
                      align={index === tableHeaders.length - 1 ? 'right' : 'center'}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableRows.map((row: any[], rowIndex: number) => (
                  <TableRow key={rowIndex}>
                    {row.map((cell: any, cellIndex: number) => (
                      <TableCell 
                        key={cellIndex}
                        align={cellIndex === row.length - 1 ? 'right' : 'center'}
                      >
                        {cell}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    );
  };
  
  // Handle before/after variant
  if (variant === 'beforeAfter' && beforeData) {
    return (
      <Box className={classes.beforeAfterContainer}>
        <Typography className={classes.sectionTitle}>
          {title}
        </Typography>
        <Box className={classes.beforeAfterTables}>
          {renderTable(beforeData, 'Before')}
          {afterData && afterData.length > 0 && renderTable(afterData, 'After')}
        </Box>
        {actionButtons && (
          <Box className={classes.actionButtons}>
            {actionButtons}
          </Box>
        )}
      </Box>
    );
  }
  
  // Handle default and referral variants
  return (
    <Box className={styles.showActions ? classes.tableWithActions : undefined}>
      {renderTable(data, title)}
      {actionButtons && styles.showActions && (
        <Box className={classes.actionButtons}>
          {actionButtons}
        </Box>
      )}
    </Box>
  );
});

// Action buttons component
const ActionButtons = React.memo<{
  action: string;
  onAction: (action: string) => void;
  loading: boolean;
  completed: boolean;
  variant?: 'contained' | 'outlined';
  color?: 'primary' | 'secondary';
}>(({ action, onAction, loading, completed, variant = 'contained', color = 'primary' }) => {
  if (completed) return null;
  
  return (
    <Button
      variant={variant}
      color={color}
      size="small"
      onClick={() => onAction(action)}
      disabled={loading}
      style={{ margin: '4px 0' }}
    >
      {loading ? <CircularProgress size={20} /> : action === 'move_giftcard_rewards' ? 'Move' : action === 'move_promo_code_rewards' ? 'Move' : 'Cancel'}
    </Button>
  );
});

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  card: {
    marginBottom: theme.spacing(3),
    maxWidth: '650px',
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  searchButton: {
    minWidth: 120,
  },
  actionContainer: {
    marginTop: theme.spacing(1),
  },
  actionButtons: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    display: 'flex',
    gap: theme.spacing(2),
    flexDirection: 'column',
    alignItems: 'center',
  },
  tableWithActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  compactTable: {
    '& .MuiTableCell-root': {
      padding: '6px 8px',
      fontSize: '0.8rem',
      whiteSpace: 'nowrap',
    },
    '& .MuiTableHead-root .MuiTableCell-root': {
      fontWeight: 600,
      backgroundColor: theme.palette.grey[100],
      padding: '8px 8px',
    },
    width: '100%',
    minWidth: '500px',
    tableLayout: 'auto',
  },
  tableContainer: {
    width: '100%',
    maxWidth: '600px',
    overflowX: 'auto',
  },
  sectionTitle: {
    fontSize: '1.1rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  rewardsCard: {
    marginBottom: theme.spacing(2),
    maxWidth: '650px',
    width: 'fit-content',
  },
  rewardsVertical: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    alignItems: 'center',
  },
  beforeAfterContainer: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  beforeAfterTables: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    justifyContent: 'center',
    flexWrap: 'nowrap',
  },
  beforeAfterTitle: {
    fontSize: '0.9rem',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.primary.main,
  },
  referralTable: {
    maxWidth: '400px',
    width: 'fit-content',
  },
  referralTableContainer: {
    maxWidth: '400px',
    width: 'fit-content',
  },
}));



const MoveAccountRewards: React.FC = () => {
  const classes = useStyles();
  const notify = useNotify();
  const [fromEmail, setFromEmail] = useState('');
  const [toEmail, setToEmail] = useState('');
  const [rewardsData, setRewardsData] = useState<{
    inflightRewards: any[][];
    giftcardRewards: any[][];
    promoCodeRewards: any[][];
    referralRewards: any[][];
  } | null>(null);
  const [beforeData, setBeforeData] = useState<{
    inflightRewards: any[][];
    giftcardRewards: any[][];
    promoCodeRewards: any[][];
    referralRewards: any[][];
  } | null>(null);
  const [afterData, setAfterData] = useState<{
    giftcard?: any[][];
    promo?: any[][];
    inflight?: any[][];
  }>({});
  const [completedActions, setCompletedActions] = useState<{
    giftcard: boolean;
    promo: boolean;
  }>({
    giftcard: false,
    promo: false,
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState({
    moveGiftcard: false,
    movePromo: false,
    cancelPromo: false
  });

  // Memoized validation
  const isValidInput = useMemo(() => fromEmail && toEmail, [fromEmail, toEmail]);

  const handleSearch = useCallback(async () => {
    if (!isValidInput) {
      notify('Both emails are required', 'error');
      return;
    }

    setLoading(true);
    try {
      const config = getConfigGet({ fromEmail, toEmail });
      const response = await axios.get(ENDPOINTS.MOVE_ACCOUNT_REWARDS, config);
      const responseData: any = response.data as any;
      if (responseData && responseData.inflightRewards && responseData.giftcardRewards && responseData.promoCodeRewards && responseData.referralRewards) {
        setRewardsData(responseData);
        setBeforeData(responseData); // Store as before data
        setAfterData({}); // Clear after data
        setCompletedActions({ giftcard: false, promo: false }); // Reset completed actions
        notify('Data loaded successfully', 'info');
      } else {
        notify('No data found', 'error');
      }
    } catch (error: any) {
      notify(error.response?.data?.message || 'Error loading data', 'error');
    } finally {
      setLoading(false);
    }
  }, [fromEmail, toEmail, isValidInput, notify]);

  const handleAction = useCallback(async (action: string) => {
    if (!isValidInput) {
      notify('Both emails are required', 'error');
      return;
    }

    const loadingKey = action === 'move_giftcard_rewards' ? 'moveGiftcard' : 
                     action === 'move_promo_code_rewards' ? 'movePromo' : 'cancelPromo';

    setActionLoading(prev => ({ ...prev, [loadingKey]: true }));
    
    let actionSuccess = false;
    let actionMessage = '';
    
    try {
      const headers = getConfigPatch();
      const data = { action, fromEmail, toEmail };
      
      const response: any = await axios.patch(ENDPOINTS.MOVE_ACCOUNT_REWARDS, data, { headers });
      
      if (response.data?.success) {
        actionSuccess = true;
        actionMessage = response.data.message || 'Action completed successfully';
      } else {
        actionSuccess = false;
        actionMessage = response.data?.message || 'Action failed';
      }
    } catch (error: any) {
      actionSuccess = false;
      actionMessage = error.response?.data?.message || 'Error performing action';
    }

    // Always fetch updated data regardless of success or failure
    try {
      const config = getConfigGet({ fromEmail, toEmail });
      const updatedResponse = await axios.get(ENDPOINTS.MOVE_ACCOUNT_REWARDS, config);
      const updatedData: any = updatedResponse.data as any;
      
      if (updatedData && updatedData.inflightRewards && updatedData.giftcardRewards && updatedData.promoCodeRewards && updatedData.referralRewards) {
        setRewardsData(updatedData);
        
        // Update specific after data based on action
        const actionMap = {
          'move_giftcard_rewards': () => {
            setAfterData(prev => ({ ...prev, giftcard: updatedData.giftcardRewards, inflight: updatedData.inflightRewards }));
            setCompletedActions(prev => ({ ...prev, giftcard: true }));
          },
          'move_promo_code_rewards': () => {
            setAfterData(prev => ({ ...prev, promo: updatedData.promoCodeRewards, inflight: updatedData.inflightRewards }));
            setCompletedActions(prev => ({ ...prev, promo: true }));
          },
          'cancel_promo_code_rewards': () => {
            setAfterData(prev => ({ ...prev, promo: updatedData.promoCodeRewards, inflight: updatedData.inflightRewards }));
            setCompletedActions(prev => ({ ...prev, promo: true }));
          }
        };
        
        actionMap[action as keyof typeof actionMap]?.();
      }
    } catch (error: any) {
      console.error('Error fetching updated data:', error);
    }

    // Show notification based on action result
    notify(actionMessage, actionSuccess ? 'info' : 'error');

    setActionLoading(prev => ({ ...prev, [loadingKey]: false }));
  }, [fromEmail, toEmail, isValidInput, notify]);

  // Memoized action handlers
  const handleMoveGiftcard = useCallback(() => handleAction('move_giftcard_rewards'), [handleAction]);
  const handleMovePromo = useCallback(() => handleAction('move_promo_code_rewards'), [handleAction]);
  const handleCancelPromo = useCallback(() => handleAction('cancel_promo_code_rewards'), [handleAction]);


  return (
    <Box className={classes.container}>
      <Typography variant="h4" gutterBottom>
        Move Account Rewards
      </Typography>

      <Card className={classes.card}>
        <CardContent>
          <Box className={classes.searchContainer}>
            <TextField
              label="Move From Email"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              fullWidth
              variant="outlined"
            />
            <TextField
              label="Move To Email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              fullWidth
              variant="outlined"
            />
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={loading || !isValidInput}
              className={classes.searchButton}
            >
              {loading ? <CircularProgress size={24} /> : 'Search'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {rewardsData && (
        <>
          {/* Total Inflight Rewards */}
          {afterData.inflight ? (
            <UnifiedTable 
              data={beforeData?.inflightRewards || []}
              beforeData={beforeData?.inflightRewards || []} 
              afterData={afterData.inflight} 
              title="Total Inflight Rewards"
              variant="beforeAfter"
            />
          ) : (
            <UnifiedTable 
              data={rewardsData?.inflightRewards || []} 
              title="Total Inflight Rewards"
              variant="default"
            />
          )}

          {/* All Three Rewards Sections - Vertical Layout */}
          <Box className={classes.rewardsVertical}>
            {/* Giftcard Rewards */}
            {afterData.giftcard ? (
              <UnifiedTable 
                data={beforeData?.giftcardRewards || []}
                beforeData={beforeData?.giftcardRewards || []} 
                afterData={afterData.giftcard} 
                title="Giftcard Rewards"
                variant="beforeAfter"
                actionButtons={
                  <ActionButtons
                    action="move_giftcard_rewards"
                    onAction={handleMoveGiftcard}
                    loading={actionLoading.moveGiftcard}
                    completed={completedActions.giftcard}
                  />
                }
              />
            ) : (
              <UnifiedTable 
                data={rewardsData?.giftcardRewards || []} 
                title="Giftcard Rewards"
                variant="default"
                actionButtons={
                  <ActionButtons
                    action="move_giftcard_rewards"
                    onAction={handleMoveGiftcard}
                    loading={actionLoading.moveGiftcard}
                    completed={completedActions.giftcard}
                  />
                }
              />
            )}

            {/* Promo Code Rewards */}
            {afterData.promo ? (
              <UnifiedTable 
                data={beforeData?.promoCodeRewards || []}
                beforeData={beforeData?.promoCodeRewards || []} 
                afterData={afterData.promo} 
                title="Promo Code Rewards"
                variant="beforeAfter"
                actionButtons={!completedActions.promo ? (
                  <>
                    <ActionButtons
                      action="cancel_promo_code_rewards"
                      onAction={handleCancelPromo}
                      loading={actionLoading.cancelPromo}
                      completed={completedActions.promo}
                      color="secondary"
                    />
                    <ActionButtons
                      action="move_promo_code_rewards"
                      onAction={handleMovePromo}
                      loading={actionLoading.movePromo}
                      completed={completedActions.promo}
                    />
                  </>
                ) : undefined}
              />
            ) : (
              <UnifiedTable 
                data={rewardsData?.promoCodeRewards || []} 
                title="Promo Code Rewards"
                variant="default"
                actionButtons={
                  <>
                    <ActionButtons
                      action="cancel_promo_code_rewards"
                      onAction={handleCancelPromo}
                      loading={actionLoading.cancelPromo}
                      completed={completedActions.promo}
                      color="secondary"
                    />
                    <ActionButtons
                      action="move_promo_code_rewards"
                      onAction={handleMovePromo}
                      loading={actionLoading.movePromo}
                      completed={completedActions.promo}
                    />
                  </>
                }
              />
            )}

            {/* Referral Rewards */}
            <UnifiedTable 
              data={rewardsData?.referralRewards || []} 
              title="Referral Rewards"
              variant="referral"
            />
          </Box>
        </>
      )}
      <Notification />
    </Box>
  );
};

export default MoveAccountRewards;
